// src/components/TurnstileWidget.tsx
//
// Wrapper React sopra il widget Cloudflare Turnstile.
// - Carica lo script /turnstile/v0/api.js una sola volta a livello globale.
// - Renderizza il widget in un <div> con render manuale ("explicit"), così
//   possiamo intercettare il token via callback e passarlo a React state.
// - In modalità "managed" (default) il widget è invisibile ~99% delle volte:
//   Cloudflare mostra un challenge SOLO se il traffico appare sospetto.
//
// Uso:
//   const [token, setToken] = useState<string | null>(null);
//   <TurnstileWidget onToken={setToken} />
//   ...
//   if (!token) return; // blocca il submit finché il widget non ha risposto
//   api.createBooking({ ...form, captchaToken: token });

import React, { useEffect, useRef } from 'react';

// Site key public — safe to embed in bundle (Turnstile richiede l'origin
// del sito per accettare il token, non serve tenerla segreta).
const SITE_KEY = '0x4AAAAAAEi8DH4xZnT2UMi6';
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Tipizzazione minimale dell'API globale window.turnstile (l'SDK non è su npm).
declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          appearance?: 'always' | 'execute' | 'interaction-only';
          size?: 'normal' | 'flexible' | 'compact';
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

interface Props {
  onToken: (token: string | null) => void;
  theme?: 'light' | 'dark' | 'auto';
}

const TurnstileWidget: React.FC<Props> = ({ onToken, theme = 'auto' }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    // 1) Carica lo script (idempotente: se già presente non lo ri-aggiunge).
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    let cancelled = false;

    const tryRender = () => {
      if (cancelled || widgetIdRef.current || !containerRef.current) return;
      if (!window.turnstile) return; // script non pronto: ritenteremo
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        theme,
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => onToken(null),
      });
    };

    // Prova subito; se lo script è ancora in fetching, ritenta ogni 200ms
    // (Turnstile non espone un evento "ready" pubblico, questa è la ricetta
    // documentata nel loro dev guide).
    tryRender();
    const interval = window.setInterval(tryRender, 200);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [theme, onToken]);

  return <div ref={containerRef} style={{ marginTop: 16 }} />;
};

export default TurnstileWidget;
